/**
 * Asset Detail Page — container with tab shell.
 * Route: /assets/:assetId  (children: general, images, clock-readings, planning,
 *                            maintenance, history, equipment-log, documents)
 *
 * Loads the full AssetDetailResult once from the server. Returns null when the
 * asset doesn't exist or is outside the user's plant access — FE redirects to
 * /no-access in that case, matching FAIM I's NoAccess → 404 pattern.
 *
 * Header layout:
 *   Left  — asset identity (code, plant, brand/model, status chip)
 *   Right — telematics last-location map card (lazy-loaded; shows "no data" state
 *           when no GPS fix exists, matching FAIM I's EquipmentDetailsDialog pattern)
 *
 * Tab navigation is URL-driven: the active tab derives from the current pathname
 * segment, and each tab is a react-router Link. Edit mode (dirty form guard) and
 * tab locking during edit are deferred to the write slice.
 */

import { lazy, Suspense, useEffect } from 'react';
import { useNavigate, useParams, useLocation, Outlet, Link as RouterLink, Navigate } from 'react-router';
import { useQuery } from '@apollo/client/react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Skeleton from '@mui/material/Skeleton';
import { graphql } from '../../gql';
import type { AssetDetailQuery } from '../../gql/graphql';
import { LoadingOverlay } from '../../components/ui';

const LocationMap = lazy(() =>
  import('../../components/ui/LocationMap').then((m) => ({ default: m.LocationMap })),
);

const ASSET_DETAIL_QUERY = graphql(`
  query AssetDetail($id: Int!) {
    assetDetail(id: $id) {
      id
      assetCode
      plantId
      plantName
      brandName
      modelName
      clusterName
      detailedClusterName
      statusLocalizationKey
      statusGroup
      isArchived
      serialNumber
      pin
      localAssetNumber
      sapOrderNumber
      equipmentNumber
      statisticalInternalOrder
      erpAssetNumber
      yearOfManufacture
      isWaterFuelTruck
      isWaterFuelTruckApplicable
      formOfFinanceName
      formOfFinanceId
      jointVenture
      jvInterest
      fleetResponsibility
      commissionDate
      projectNumber
      warrantyName
      includeInPlan
      serviceContractUnknown
      serviceProviderTypeName
      serviceDealerName
      serviceCompanyName
      serviceStartDate
      serviceEndDate
      serviceTypeName
      totalMRHours
      serviceCostEnabled
      serviceCostDetails
      utilizationName
      payloadInM3
      tyreSize
      machineName
      engineEmissionStandardName
      telematicsSupport
      telematicsTypeName
      telematicsCode
    }
  }
`);

/** The shape of the asset detail data available to child tab routes via Outlet context. */
export type AssetDetailFragment = NonNullable<AssetDetailQuery['assetDetail']>;

const ASSET_LAST_LOCATION_QUERY = graphql(`
  query AssetLastLocation($assetId: Int!) {
    assetLastLocation(assetId: $assetId) {
      latitude
      longitude
      readingDate
    }
  }
`);

const TABS = [
  { value: 'general', label: 'Details' },
  { value: 'images', label: 'Images' },
  { value: 'clock-readings', label: 'Clock Readings' },
  { value: 'planning', label: 'Planning Details' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'history', label: 'Service tracker' },
  { value: 'equipment-log', label: 'Equipment Log' },
  { value: 'documents', label: 'Documents' },
] as const;

export function AssetDetailPage() {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const id = Number(assetId);

  const { data, loading } = useQuery(ASSET_DETAIL_QUERY, {
    variables: { id },
    skip: !id || isNaN(id),
  });

  const { data: locationData, loading: locationLoading } = useQuery(ASSET_LAST_LOCATION_QUERY, {
    variables: { assetId: id },
    // Fire only after the main query confirms the asset is accessible — avoids
    // a second round-trip for invalid/no-access assets and matches the agreed
    // "lazy, fired after page loads" behavior.
    skip: !data?.assetDetail,
  });

  // Once data loads, update the URL with the asset code so the breadcrumb
  // can display it instead of the numeric ID. Matches the FAIM2 admin pattern
  // where the user name is passed via search params.
  useEffect(() => {
    if (data?.assetDetail?.assetCode) {
      const current = new URLSearchParams(location.search);
      const code = data.assetDetail.assetCode;
      if (current.get('code') !== code) {
        navigate({ search: `?code=${encodeURIComponent(code)}` }, { replace: true });
      }
    }
    // Only run when assetCode changes, not on every location change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.assetDetail?.assetCode]);

  // Redirect for invalid/missing asset IDs (malformed URLs) — after all hooks.
  if (!id || isNaN(id)) {
    return <Navigate to="/no-access" replace />;
  }

  // Navigate away when access is denied (null result from server).
  if (!loading && data !== undefined && data.assetDetail === null) {
    return <Navigate to="/no-access" replace />;
  }

  // Derive the active tab from the current URL segment.
  const segments = location.pathname.split('/');
  const currentTab = segments[segments.length - 1];
  const activeTab = TABS.some((t) => t.value === currentTab) ? currentTab : 'general';

  const asset = data?.assetDetail;

  return (
    <main className="max-w-[1440px] mx-auto px-6 py-6">
      <LoadingOverlay visible={loading} />

      {/* ── Page header: asset identity + map placeholder ──────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-4 mb-6">
        {/* Left — asset identity */}
        <Card>
          <CardContent>
            <div className="flex items-start justify-between gap-4">
              <div>
                <Typography variant="h1" component="h1" data-testid="asset-code">
                  {asset?.assetCode ?? '—'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} data-testid="asset-plant">
                  {asset?.plantName}
                </Typography>
                {asset && (
                  <Typography variant="body2" color="text.secondary" data-testid="asset-brand-model">
                    {asset.brandName} · {asset.modelName} · {asset.clusterName}
                  </Typography>
                )}
              </div>
              {asset?.isArchived && (
                <Chip label="Archived" size="small" color="warning" variant="outlined" />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Right — telematics last-location map */}
        <Card sx={{ minHeight: 200 }}>
          <CardContent className="h-full" sx={{ p: '0 !important', minHeight: 200 }}>
            {locationLoading ? (
              <Skeleton variant="rectangular" width="100%" height={200} />
            ) : locationData?.assetLastLocation ? (
              <Suspense fallback={<Skeleton variant="rectangular" width="100%" height={200} />}>
                <LocationMap
                  lat={Number(locationData.assetLastLocation.latitude)}
                  lng={Number(locationData.assetLastLocation.longitude)}
                  label={`Last seen: ${new Date(String(locationData.assetLastLocation.readingDate)).toLocaleString()}`}
                  style={{ height: 200, width: '100%' }}
                />
              </Suspense>
            ) : (
              <div className="h-full flex items-center justify-center" style={{ minHeight: 200 }}>
                <Typography variant="body2" color="text.secondary" data-testid="map-no-data">
                  No location data available
                </Typography>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ── Tab navigation + content (only once data has arrived) ──────── */}
      {asset != null && (
        <>
          <Tabs
            value={activeTab}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}
            data-testid="asset-tabs"
          >
            {TABS.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={tab.label}
                component={RouterLink}
                to={tab.value}
              />
            ))}
          </Tabs>

          {/* ── Tab content (child routes via Outlet) ──────────────────── */}
          <Outlet context={asset} />
        </>
      )}
    </main>
  );
}

/**
 * Default child for the /assets/:assetId index route.
 * Immediately redirects to the General tab.
 */
export function AssetDetailIndexRedirect() {
  return <Navigate to="general" replace />;
}

